import {TokenError} from "@App/apis/rest/ayvens/token/tokenError";
import type {TokenSuccessResponse} from "@App/apis/rest/ayvens/token/tokenSuccessResponse";
import {TokenErrorResponse} from "@App/apis/rest/ayvens/token/tokenErrorResponse";

/**
 * TokenService handles creating a token against a /token endpoint.
 */
export class TokenService {
    private readonly baseUrl: string;

    /**
     * Constructs an instance of AuthService.
     * @param baseUrl - The base URL for the API (e.g., "http://localhost:3000").
     *                  If empty, "/login" will be treated as a relative path.
     */
    constructor(baseUrl = '') {
        this.baseUrl = baseUrl;
    }

    /**
     * Authenticates a user with the provided email and password.
     * @param uuid - The user's uuid.
     * @param numberPlate - The numberplate to create a token for.
     * @returns A Promise that resolves with the user ID (string) upon successful authentication.
     * @throws {AuthenticationError} if authentication fails (401).
     * @throws {Error} for network issues or other server errors.
     */
    public async createToken(uuid: string, numberPlate: string): Promise<string> {
        const payload: TokenPayload = { uuid, numberPlate };

        const tokenUrl = this.baseUrl ? `${this.baseUrl}/rest/repareo/frontend/v1/ayvens/token` : '/rest/repareo/frontend/v1/ayvens/token';

        let response: Response;
        try {
            response = await fetch(tokenUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json', // Good practice to specify expected response type
                },
                body: JSON.stringify(payload),
            });
        } catch (networkError) {
            // This catches errors from fetch itself (e.g., network down, DNS issue)
            // These are typically TypeError
            throw new Error(`Network error: Unable to reach the login service. Original: ${(networkError as Error).message}`);
        }

        if (response.status === 200) {
            try {
                const data: TokenSuccessResponse = await response.json();
                // Validate that userId is a non-empty string as expected for a UUIDv4
                if (data && typeof data.token === 'string' && data.token.length > 0) {
                    return data.token;
                }

                throw new Error('Create Token successful, but received invalid or missing userId in response.');
            } catch (jsonError) {
                // This catches errors from response.json() if the body is not valid JSON
                throw new Error(`Failed to parse successful login response. Original: ${(jsonError as Error).message}`);
            }
        }

        if (response.status === 401) {
            const data: TokenErrorResponse = await response.json();
            throw new TokenError(data);
        }

        if (response.status === 404) {
            const data: TokenErrorResponse = await response.json();
            throw new TokenError(data);
        }

        // Handle other non-ok statuses (e.g., 400 Bad Request, 500 Internal Server Error)
        const errorText = await response.text().catch(() => `Status ${response.status} with no error body.`);
        throw new Error(`Create Token request failed with status ${response.status}: ${errorText}`);
    }
}
